'use client';

import CloseIcon from '@mui/icons-material/Close';
import MenuIcon from '@mui/icons-material/Menu';
import { Box, IconButton, Menu, MenuItem } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { FC, MouseEvent, useContext, useMemo, useState } from 'react';

import { useFullUser } from '../../../hooks/auth/use-user';
import { useBoolean } from '../../../hooks/use-boolean';
import { useLocale } from '../../../hooks/use-locale';
import { useTranslations } from '../../../hooks/use-translations';
import { ColorModeContext } from '../../../theme/ColorContext';
import { MenuItemContent } from '../../common';
import MerchantInfo from '../../common/MerchantInfo/MerchantInfo';
import { useAuthMenu } from '../../common/Sidebar/use-auth-menu';
import { useGuestMenu } from '../../common/Sidebar/use-guest-menu';
import { useNavMenu } from '../../common/Sidebar/use-nav-menu';
import { UserInfoCard, UserInfoLanguagePopup } from '../../common/UserInfo';
import { DarkIcon, LightIcon, RuFlag, UsFlag } from '../../icons';
import { Balance } from '../Balance';

import styles from './Navigation.module.scss';

export type NavigationProps = {
  isAuthorized: boolean;
};

export const Navigation: FC<NavigationProps> = (props) => {
  const { isAuthorized } = props;

  const t = useTranslations('user.contract');

  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const { userInfo, merchantInfo } = useFullUser();
  const locale = useLocale();
  const colorMode = useContext(ColorModeContext);

  const [language, setLanguage] = useState(locale);
  const { value: languageModalOpen, setTrue: openLanguageModal, setFalse: closeLanguageModal } = useBoolean(false);
  const theme = useTheme();

  const guestMenu = useGuestMenu();
  const authMenu = useAuthMenu();
  const navMenu = useNavMenu();

  const handleOpenNavMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const currentFlagComponent = useMemo(() => {
    if (locale === 'ru') {
      return <RuFlag />;
    }

    if (locale === 'en') {
      return <UsFlag />;
    }

    throw new Error('Unsupported language flag');
  }, [locale]);

  return (
    <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
      <IconButton
        size="large"
        aria-label="account of current user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={handleOpenNavMenu}
        color="inherit"
        style={{
          marginLeft: 'auto',
        }}
      >
        {Boolean(anchorElNav) ? <CloseIcon /> : <MenuIcon />}
      </IconButton>

      <Menu
        id="menu-appbar"
        className={styles.menu}
        anchorEl={anchorElNav}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
          sx: { padding: '0' },
        }}
        slotProps={{
          paper: {
            style: {
              position: 'absolute',
              width: '100%',
              borderRadius: 0,
              top: '64px',
              height: 'calc(100% - 64px)',
              padding: '2rem',
              backgroundColor: theme.palette.background.default,
            },
          },
        }}
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={Boolean(anchorElNav)}
        onClose={handleCloseNavMenu}
        sx={{
          display: { xs: 'block', md: 'none' },
        }}
      >
        {isAuthorized
          ? [
              <MenuItem
                key="balance"
                sx={{
                  margin: '0 0 1rem 0',
                }}
              >
                <Balance />
              </MenuItem>,

              <MenuItem
                key="merchantInfo"
                sx={{
                  margin: '0 0 2rem 0',
                }}
              >
                <MerchantInfo userInfo={userInfo} merchantInfo={merchantInfo} />
              </MenuItem>,

              navMenu.map((listItem, index) => (
                <MenuItem key={index}>
                  <MenuItemContent item={listItem} />
                </MenuItem>
              )),

              authMenu.map((listItem, index) => (
                <MenuItem key={index}>
                  <MenuItemContent item={listItem} />
                </MenuItem>
              )),

              <MenuItem key="userInfo" className={styles.userInfo}>
                <UserInfoCard userInfo={userInfo} />
              </MenuItem>,

              <MenuItem
                key="lang"
                sx={{
                  margin: '0 0 1rem 0',
                }}
                className={styles.userOptions}
                onClick={openLanguageModal}
              >
                <p>
                  {currentFlagComponent}
                  <span className={styles.upper}>{locale}</span>
                </p>
              </MenuItem>,

              <MenuItem
                key="theme"
                sx={{
                  margin: '0 0 1rem 0',
                }}
                className={styles.userOptions}
                onClick={colorMode.toggleColorMode}
              >
                <p>
                  {theme.palette.mode === 'dark' ? (
                    <>
                      <LightIcon /> <span>{t('theme.light')}</span>
                    </>
                  ) : (
                    <>
                      <DarkIcon /> <span>{t('theme.dark')}</span>
                    </>
                  )}
                </p>
              </MenuItem>,
            ]
          : guestMenu.map((listItem, index) => (
              <MenuItem key={index}>
                <MenuItemContent item={listItem} />
              </MenuItem>
            ))}
      </Menu>

      {isAuthorized && (
        <UserInfoLanguagePopup
          setLanguage={setLanguage}
          language={language}
          handleCloseModal={closeLanguageModal}
          modalOpen={languageModalOpen}
        />
      )}
    </Box>
  );
};
